import { useEffect, useState, useCallback, useRef } from 'react';
import { getCases } from 'api/cases';
import { useNotificationProvider } from 'hooks';
import { Case, CaseFilter } from 'containers/pages/cases/types';

const DEBOUNCE_MS = 300;

const initFilter: CaseFilter = {
  SearchKeyword: '',
  CustomerId: null,
  ConsultantId: null,
  GroupId: null,
  ProjectId: null,
  DeliverableId: null,
  Status: [],
  Date: '',
};

function useCaseDashboard() {
  const { notifyError } = useNotificationProvider();

  const [isLoading, setIsLoading] = useState(false);
  const [cases, setCases] = useState<Case[]>([]);
  const [filteredCases, setFilteredCases] = useState<Case[]>([]);
  const [searchInput, setSearchInput] = useState('');
  const [filters, setFilters] = useState<CaseFilter>(initFilter);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchCases = useCallback(async (currentFilters: CaseFilter) => {
    setIsLoading(true);
    try {
      const payload: Record<string, unknown> = {
        SearchKeyword: currentFilters.SearchKeyword || '',
        Page: 1,
        Take: 1000,
      };
      if (currentFilters.CustomerId) payload.CustomerId = currentFilters.CustomerId;
      if (currentFilters.ConsultantId) payload.ConsultantId = currentFilters.ConsultantId;
      if (currentFilters.GroupId) payload.GroupId = currentFilters.GroupId;
      if (currentFilters.ProjectId) payload.ProjectId = currentFilters.ProjectId;
      if (currentFilters.DeliverableId) payload.DeliverableId = currentFilters.DeliverableId;
      if (currentFilters.Status.length > 0) payload.Status = currentFilters.Status;
      if (currentFilters.Date) payload.Date = currentFilters.Date;

      const result = await getCases(payload);
      const data: Case[] = Array.isArray(result) ? result : result?.Data || [];
      setCases(data);
      setFilteredCases(data);
    } catch (error) {
      console.error('[useCaseDashboard] Fetch failed:', error);
      notifyError('Failed to load cases');
    } finally {
      setIsLoading(false);
    }
  }, [notifyError]);

  // Initial load
  useEffect(() => {
    fetchCases(initFilter);
  }, [fetchCases]);

  // Client-side search filtering
  const applySearch = useCallback((keyword: string, allCases: Case[]) => {
    if (!keyword.trim()) {
      setFilteredCases(allCases);
      return;
    }
    const lower = keyword.toLowerCase();
    const filtered = allCases.filter(
      (c) =>
        c.Title?.toLowerCase().includes(lower) ||
        c.CustomerName?.toLowerCase().includes(lower) ||
        c.ConsultantName?.toLowerCase().includes(lower) ||
        c.StatusTitle?.toLowerCase().includes(lower) ||
        c.Description?.toLowerCase().includes(lower),
    );
    setFilteredCases(filtered);
  }, []);

  const onSearchInput = (input: string) => {
    setSearchInput(input);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      applySearch(input, cases);
    }, DEBOUNCE_MS);
  };

  const onResetSearch = () => {
    setSearchInput('');
    setFilteredCases(cases);
  };

  const onFilterChange = (field: keyof CaseFilter, value: unknown) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const onApplyFilters = () => {
    const merged = { ...filters, SearchKeyword: searchInput };
    fetchCases(merged);
  };

  const onClearFilters = () => {
    setFilters(initFilter);
    setSearchInput('');
    fetchCases(initFilter);
  };

  const state = {
    isLoading,
    cases: filteredCases,
    searchInput,
    filters,
  };

  const handler = {
    onSearchInput,
    onResetSearch,
    onFilterChange,
    onApplyFilters,
    onClearFilters,
    fetchCases,
  };

  return { state, handler };
}

export default useCaseDashboard;
